import React, { useState } from 'react';
import { collection, doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';

export default function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('student');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Normalize email for document ID (lowercase, trim, replace . with ,)
  const normalizeEmail = (email) => {
    return email.trim().toLowerCase().replace(/\./g, ',');
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!role) {
      setError('Please select your role');
      return;
    }

    setSubmitting(true);

    try {
      const normalizedEmail = normalizeEmail(email);
      const waitlistRef = doc(db, 'waitlist', normalizedEmail);

      // Check if already exists
      const existingDoc = await getDoc(waitlistRef);
      if (existingDoc.exists()) {
        setSubmitted(true);
        toast.success("You're already on the waitlist ✅");
        setSubmitting(false);
        return;
      }

      // Create new waitlist entry
      await setDoc(waitlistRef, {
        email: email.trim().toLowerCase(),
        role: role,
        createdAt: serverTimestamp(),
        source: 'coming-soon',
      });

      setSubmitted(true);
      setEmail('');
      toast.success("✅ You're on the list. We'll notify you before launch.");
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      setError('Something went wrong. Please try again.');
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-success-50 border-2 border-success-200 rounded-2xl p-8 text-center">
        <CheckCircle className="w-12 h-12 text-success-600 mx-auto mb-4" />
        <p className="text-lg font-semibold text-success-800 mb-2">
          ✅ You're on the list. We'll notify you before launch.
        </p>
        <p className="text-sm text-success-700">
          No spam. We'll only email you about StudyStay.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-3">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError('');
          }}
          placeholder="Enter your email"
          required
          className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 transition-all duration-200 text-base"
          aria-label="Email address"
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? 'email-error' : undefined}
        />
        {error && (
          <p id="email-error" className="text-sm text-error-600" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setRole('student')}
          className={`px-6 py-4 rounded-xl font-semibold transition-all duration-200 ${
            role === 'student'
              ? 'bg-primary-500 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-300'
          }`}
        >
          Student
        </button>
        <button
          type="button"
          onClick={() => setRole('landlord')}
          className={`px-6 py-4 rounded-xl font-semibold transition-all duration-200 ${
            role === 'landlord'
              ? 'bg-primary-500 text-white shadow-lg scale-105'
              : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-primary-300'
          }`}
        >
          Landlord
        </button>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Joining...</span>
          </>
        ) : (
          <>
            <Mail className="w-5 h-5" />
            <span>Join Waitlist</span>
          </>
        )}
      </button>

      <p className="text-sm text-gray-500 text-center">
        No spam. We'll only email you about StudyStay.
      </p>
    </form>
  );
}

